import type { Middleware } from "@reduxjs/toolkit";

import { getApiBasePath } from "@/hooks/fetchOptions";

/**
 * Frontend error beacon (UI half). The server is BLIND to a whole class of
 * failure: a response that is valid JSON but fails the generated
 * `Convert.toXxx` validation throws inside RTK Query's `transformResponse`, the
 * query flips to `isError`, the page breaks — and the handler logged a 200, so
 * Grafana stays silent (this is exactly how the daily-digest `adr: null` bug
 * reached a client undetected on 2026-06-08).
 *
 * This middleware is the central listener for that class. It reports ONLY the
 * browser-only failures the server cannot see, and deliberately ignores ordinary
 * HTTP 4xx/5xx (already logged server-side) and aborted/skipped queries. Reports
 * go to `POST {apiBase}/client-errors` (apiBase is `/api`, or `/api/demo` on a
 * demo page), which logs them at ERROR level → the existing
 * `ErrorLogDetected` Grafana alert. De-duped by signature + capped per session so
 * a render loop cannot spam the alert.
 */

interface ClientErrorPayload {
    path: string;
    name: string;
    message: string;
    stack?: string;
    status?: number;
}

const reportedSignatures = new Set<string>();
let sentThisSession = 0;
const MAX_REPORTS_PER_SESSION = 25;

/** Test-only: reset the per-session de-dupe state between cases. */
export function resetClientErrorReporter(): void {
    reportedSignatures.clear();
    sentThisSession = 0;
}

/**
 * Fire-and-forget POST of a single client error. Never throws and never blocks
 * the UI; suppresses identical repeats and caps total sends per session.
 */
export function reportClientError(payload: ClientErrorPayload): void {
    const signature = `${payload.path}|${payload.name}|${payload.message}`.slice(0, 300);
    if (reportedSignatures.has(signature) || sentThisSession >= MAX_REPORTS_PER_SESSION) {
        return;
    }
    reportedSignatures.add(signature);
    sentThisSession += 1;

    const body = JSON.stringify({
        ...payload,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    });
    // Follow the active API base path: on a /demo page this is "/api/demo", so
    // the beacon hits the unauthenticated demo route instead of 401-ing against
    // the authenticated "/api/client-errors" (which would silently drop the
    // report — the very failure this beacon exists to surface).
    void fetch(`${getApiBasePath()}/client-errors`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
    }).catch(() => {
        /* Reporting failures must stay silent — never surface to the user. */
    });
}

interface RejectedRtkAction {
    type: string;
    error?: { name?: string; message?: string; stack?: string };
    payload?: { status?: number | string };
    meta?: {
        arg?: { endpointName?: string };
        rejectedWithValue?: boolean;
        aborted?: boolean;
        condition?: boolean;
    };
}

// Narrow an arbitrary action to an RTK *Query* rejected action. RTK Query thunks
// are the only rejected actions that carry meta.arg.endpointName, so this both
// type-guards and filters out non-data-layer actions.
function asRejectedRtkQuery(action: unknown): RejectedRtkAction | undefined {
    if (typeof action !== "object" || action === null) {
        return undefined;
    }
    const candidate = action as RejectedRtkAction;
    if (typeof candidate.type !== "string" || !candidate.type.endsWith("/rejected")) {
        return undefined;
    }
    if (typeof candidate.meta?.arg?.endpointName !== "string") {
        return undefined;
    }
    return candidate;
}

export const clientErrorReporter: Middleware = () => (next) => (action) => {
    const rejected = asRejectedRtkQuery(action);
    if (rejected) {
        const { meta, error, payload } = rejected;
        const aborted =
            meta?.aborted === true || error?.name === "AbortError" || error?.name === "ConditionError";
        if (!aborted) {
            // PARSING_ERROR/CUSTOM_ERROR: fetchBaseQuery couldn't parse the body.
            // A throw (e.g. Convert validation) rejects WITHOUT a value — that is
            // the browser-only class. Ordinary HTTP errors set rejectedWithValue
            // true with a numeric status and are skipped (already logged server-side).
            const status = payload?.status;
            const isParsingError = status === "PARSING_ERROR" || status === "CUSTOM_ERROR";
            const isThrow = meta?.rejectedWithValue !== true;
            if (isParsingError || isThrow) {
                reportClientError({
                    path: typeof window !== "undefined" ? window.location.pathname : "",
                    name: meta?.arg?.endpointName ?? "unknown",
                    message: error?.message ?? (typeof status === "string" ? status : "client request failed"),
                    stack: error?.stack,
                });
            }
        }
    }
    return next(action);
};
